import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { Alert, AlertType, Error, Order } from '../types/common';
import { wait } from '../utils/helpers';
import { CREATE_ORDER_ERROR_MESSAGE, FETCH_ORDERS_ERROR_MESSAGE } from '../constants/messages';
import { getCsrfToken } from '../utils/helpers';

export type CommonState = {
    alert: Alert;
    orders: Order[];
    isLoading: boolean;
    error: Error;
};

const initialState: CommonState = {
    alert: {
        type: AlertType.Info,
        message: '',
    },
    orders: [],
    isLoading: false,
    error: {
        isError: false,
        message: '',
    },
};

const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

type ApiOrder = Omit<Order, 'id'> & { _id: string };

// ====== helpers для purchase snapshot ======
type YmPurchaseProduct = { id: string; name: string; price: number; quantity: number };
type YmPurchaseSnapshot = {
    v: 2;
    orderNumber: string;
    paymentId: string;
    total: number | null;
    products: YmPurchaseProduct[];
    productIds: string[];
    createdAt: number;
    userId?: string;
};
const YM_PURCHASE_SNAPSHOT_KEY = 'yoo_purchase_snapshot_v1';
function toFiniteNumber(v: unknown): number | null {
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function buildYmProductsFromOrder(order: Partial<Order>): YmPurchaseProduct[] {
    const rawCart = (order as any)?.cart;
    const cart = Array.isArray(rawCart) ? (rawCart as any[]) : [];
    if (!cart.length) return [];
    const products: YmPurchaseProduct[] = cart
        .map((it) => {
            const id = String(it?.productId ?? it?.id ?? '').trim();
            const name = String(it?.name ?? it?.title ?? '').trim();
            const qtyRaw = Number(it?.quantity);
            const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? Math.floor(qtyRaw) : 1;
            const totalPrice = toFiniteNumber(it?.totalPrice) ?? null;
            const discounted = toFiniteNumber(it?.discountedPrice) ?? null;
            const special = toFiniteNumber(it?.specialDiscountedPrice) ?? null;
            const base = toFiniteNumber(it?.price) ?? null;
            let unitPrice: number | null = null;
            if (totalPrice != null && quantity > 0) {
                unitPrice = +(totalPrice / quantity).toFixed(2);
            } else if (special != null) {
                unitPrice = +special.toFixed(2);
            } else if (discounted != null) {
                unitPrice = +discounted.toFixed(2);
            } else if (base != null) {
                unitPrice = +base.toFixed(2);
            }
            if (!id || !name) return null;
            if (unitPrice == null || !Number.isFinite(unitPrice) || unitPrice <= 0) return null;
            return { id, name, price: unitPrice, quantity };
        })
        .filter(Boolean) as YmPurchaseProduct[];
    return products;
}
function buildOrderedProductIdsFromOrder(order: Partial<Order>): string[] {
    const rawCart = (order as any)?.cart;
    const cart = Array.isArray(rawCart) ? (rawCart as any[]) : [];
    const ids = cart.map((it) => String(it?.productId ?? it?.id ?? '').trim()).filter(Boolean);
    return Array.from(new Set(ids));
}
function resolveOrderUserId(order: Partial<Order>): string {
    const raw = (order as { userID?: unknown } | null | undefined)?.userID;
    return String(raw ?? '').trim();
}

export const fetchOrders = createAsyncThunk('common/fetchOrders', async (_, { dispatch, rejectWithValue }) => {
    const response = await fetch(`${BASE_URL}/orders`, { credentials: 'include' });
    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: FETCH_ORDERS_ERROR_MESSAGE }));
        return rejectWithValue(FETCH_ORDERS_ERROR_MESSAGE);
    }
    const data = (await response.json()) as ApiOrder[];
    // Преобразуем `_id` в `id` для совместимости с компонентом
    const orders: Order[] = data.map(({ _id, ...rest }) => ({
        id: _id,
        ...rest,
    }));
    return orders;
});
export const fetchMyOrders = createAsyncThunk('common/fetchOrders/my', async (_, { dispatch, rejectWithValue }) => {
    const response = await fetch(`${BASE_URL}/orders/my`, { credentials: 'include' });
    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: FETCH_ORDERS_ERROR_MESSAGE }));
        return rejectWithValue(FETCH_ORDERS_ERROR_MESSAGE);
    }
    const data = (await response.json()) as ApiOrder[];
    // Преобразуем `_id` в `id` для совместимости с компонентом
    const orders: Order[] = data.map(({ _id, ...rest }) => ({
        id: _id,
        ...rest,
    }));
    return orders;
});

type CreateOrderPayload = Partial<Order> & {
    payWithSBP?: boolean;
    payWithInstallments?: boolean;
    deliveryMethod?: 'pickup' | 'dpd' | 'dtl';
    shippingCost?: number;
};

export const createOrder = createAsyncThunk('common/createOrder', async (order: CreateOrderPayload, { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const idempotencyKey = crypto.randomUUID(); // уникальный ключ для идемпотентности
    const response = await fetch(`${BASE_URL}/orders`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
        body: JSON.stringify({ ...order, idempotencyKey }),
    });
    if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
        const errorMessage = data.error || data.message || CREATE_ORDER_ERROR_MESSAGE;
        dispatch(showAlert({ type: AlertType.Error, message: errorMessage }));
        return rejectWithValue(errorMessage);
    }
    const data = await response.json();
    if (data.confirmationUrl) {
        sessionStorage.setItem('yoo_payment_id', data.paymentId);
        sessionStorage.setItem('yoo_order_num', String(data.orderNumber));
        try {
            const products = buildYmProductsFromOrder(order);
            const productIds = buildOrderedProductIdsFromOrder(order);
            const total = toFiniteNumber((order as any)?.totalPrice) ?? null;
            const snapshotUserId = resolveOrderUserId(order);
            const snap: YmPurchaseSnapshot = {
                v: 2,
                orderNumber: String(data.orderNumber ?? ''),
                paymentId: String(data.paymentId ?? ''),
                total,
                products,
                productIds,
                createdAt: Date.now(),
                userId: snapshotUserId || undefined,
            };
            sessionStorage.setItem(YM_PURCHASE_SNAPSHOT_KEY, JSON.stringify(snap));
            localStorage.setItem(YM_PURCHASE_SNAPSHOT_KEY, JSON.stringify(snap));
        } catch {
            // игнорируем
        }
        window.location.href = data.confirmationUrl;
    }
    // Преобразуем `_id` в `id` для совместимости с компонентом
    return { id: data.id, orderNumber: data.orderNumber, orderStatus: data.orderStatus ?? data.status, confirmationUrl: data.confirmationUrl, ...order } as Order;
});

export const updateOrderStatus = createAsyncThunk(
    'common/updateOrderStatus',
    async ({ id, orderStatus }: { id: string; orderStatus: string }, { dispatch, rejectWithValue }) => {
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${BASE_URL}/orders/${id}/status`, {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'csrf-token': csrfToken,
            },
            body: JSON.stringify({ orderStatus }),
        });
        if (!response.ok) {
            return rejectWithValue('Ошибка при обновлении статуса заказа');
        }
        const data = await response.json();
        return { id, orderStatus: data.orderStatus };
    },
);

export const updateOrderDetails = createAsyncThunk(
    'common/updateOrderDetails',
    async ({ id, address, orderComment }: { id: string; address?: null | { raw: string; struct: any }; orderComment?: string }, { dispatch, rejectWithValue }) => {
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${BASE_URL}/orders/${id}/details`, {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'csrf-token': csrfToken,
            },
            body: JSON.stringify({ address, orderComment }),
        });
        if (!response.ok) {
            return rejectWithValue('Ошибка при обновлении деталей заказа');
        }
        const data = await response.json();
        return { id, address: data.address as null | { raw: string; struct: any }, orderComment: data.orderComment };
    },
);

export const showAlert = createAsyncThunk('common/handleAlert', async (alert: Alert, { dispatch }) => {
    dispatch(setAlert(alert));
    await wait(5000);
    dispatch(hideAlert());
});

export const commonSlice = createSlice({
    name: 'common',
    initialState,
    reducers: {
        setAlert: (state, action: PayloadAction<Alert>) => {
            state.alert = action.payload;
        },
        hideAlert: (state) => {
            state.alert = initialState.alert;
        },
    },

    extraReducers: (builder) => {
        builder.addCase(fetchOrders.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchOrders.fulfilled, (state, { payload }) => {
            state.orders = payload;
            state.isLoading = false;
        });
        builder.addCase(fetchOrders.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });
        builder.addCase(createOrder.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(createOrder.fulfilled, (state, { payload }) => {
            state.orders.push(payload);
            state.isLoading = false;
        });
        builder.addCase(createOrder.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });
        builder.addCase(updateOrderStatus.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(updateOrderStatus.fulfilled, (state, { payload }) => {
            const index = state.orders.findIndex((order) => order.id === payload.id);
            if (index !== -1) {
                state.orders[index].orderStatus = payload.orderStatus;
            }
            state.isLoading = false;
        });
        builder.addCase(updateOrderStatus.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as string,
            };
            state.isLoading = false;
        });
        builder.addCase(updateOrderDetails.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(updateOrderDetails.fulfilled, (state, { payload }) => {
            const index = state.orders.findIndex((order) => order.id === payload.id);
            if (index !== -1) {
                if (payload.address !== undefined) {
                    state.orders[index].user.address = payload.address ?? null;
                }
                if (payload.orderComment) {
                    state.orders[index].orderComment = payload.orderComment;
                }
            }
            state.isLoading = false;
        });
        builder.addCase(updateOrderDetails.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as string,
            };
            state.isLoading = false;
        });
    },
});

export const { setAlert, hideAlert } = commonSlice.actions;
export default commonSlice.reducer;
